import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { getDeploymentStatus } from "../../services/crmService";

function normalizeStatus(value = "") {
    return String(value || "unknown")
        .replace(/[_-]/g, " ")
        .trim();
}

function getStatusTone(status = "") {
    const clean = String(status || "").toLowerCase();

    if (["completed", "success", "published", "ready", "idle"].includes(clean)) return "success";
    if (["pending", "queued", "running", "processing", "deploying"].includes(clean)) return "warning";
    if (["failed", "error"].includes(clean)) return "danger";

    return "neutral";
}

function formatDate(value) {
    if (!value) return "—";

    try {
        return new Intl.DateTimeFormat("en-IN", {
            dateStyle: "medium",
            timeStyle: "short"
        }).format(new Date(value));
    } catch {
        return "—";
    }
}

export default function DeploymentStatusPanel({
    projectId,
    title = "Deployment Status",
    compact = false,
    autoRefresh = false,
    refreshMs = 10000,
    className = ""
}) {
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null);

    const load = useCallback(async ({ silent = false } = {}) => {
        if (!projectId) return;

        try {
            if (!silent) setLoading(true);

            const data = await getDeploymentStatus(projectId);
            setStatus(data || null);
        } catch (error) {
            console.error(error);
            if (!silent) toast.error("Failed to load deployment status.");
        } finally {
            if (!silent) setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!autoRefresh || !projectId) return undefined;

        const timer = window.setInterval(() => {
            load({ silent: true });
        }, refreshMs);

        return () => window.clearInterval(timer);
    }, [autoRefresh, projectId, refreshMs, load]);

    const statusText =
        status?.status ||
        status?.Status ||
        status?.deploymentStatus ||
        status?.DeploymentStatus ||
        status?.lastStatus ||
        status?.LastStatus ||
        "Unknown";

    const tone = getStatusTone(statusText);

    const lastUpdated =
        status?.updatedAtUtc ||
        status?.UpdatedAtUtc ||
        status?.lastUpdatedAtUtc ||
        status?.LastUpdatedAtUtc ||
        status?.completedAtUtc ||
        status?.CompletedAtUtc ||
        status?.createdAtUtc ||
        status?.CreatedAtUtc;

    const liveUrl =
        status?.liveUrl ||
        status?.LiveUrl ||
        status?.url ||
        status?.Url ||
        "";

    const message =
        status?.message ||
        status?.Message ||
        status?.errorMessage ||
        status?.ErrorMessage ||
        status?.summary ||
        status?.Summary ||
        "Deployment status is available for this project.";

    return (
        <section className={`deployment-status-panel ${compact ? "compact" : ""} ${className}`}>
            <div className="deployment-status-head">
                <div>
                    <span className="project-kicker">Live Operations</span>
                    <h2>{title}</h2>
                    <p>{message}</p>
                </div>

                <button
                    type="button"
                    className="project-action-btn compact secondary"
                    onClick={() => load()}
                    disabled={loading}
                >
                    {loading ? "Checking..." : "Refresh"}
                </button>
            </div>

            <div className="deployment-status-grid">
                <div>
                    <small>Status</small>
                    <strong className={`deployment-status-pill ${tone}`}>
                        {normalizeStatus(statusText)}
                    </strong>
                </div>

                <div>
                    <small>Last Updated</small>
                    <strong>{formatDate(lastUpdated)}</strong>
                </div>

                <div>
                    <small>Live Site</small>
                    {liveUrl ? (
                        <a href={liveUrl} target="_blank" rel="noreferrer">
                            Open live site →
                        </a>
                    ) : (
                        <strong>Not published yet</strong>
                    )}
                </div>
            </div>
        </section>
    );
}